import type { EquipmentCode } from "@/faults-app/lib/db";

/*
  مكتبة VFD محلية موسعة، مضافة فوق بيانات Supabase بدون تغيير واجهة الموقع.
  الرموز/العناوين مأخوذة من مراجع الشركات المذكورة في VFD_COVERAGE.md.
  الشرح العربي وخطوات الفحص هي إرشادات تشخيصية مبنية على نوع العطل، وليست اقتباساً حرفياً من الـManual.
*/

type RawCode = readonly [code: string, title: string];

const SAFETY = "افصل التغذية واتبع زمن تفريغ مكثفات الـDC Bus المحدد من الشركة قبل لمس أطراف القدرة. القياسات الحية ينفذها فني مؤهل فقط.";

function slugPart(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "code";
}

function meaningAr(title: string) {
  const t = title.toLowerCase();
  const map: Array<[string, string]> = [
    ["overcurrent", "زيادة تيار"], ["over-current", "زيادة تيار"], ["short circuit", "قصر كهربائي"],
    ["ground fault", "عطل تأريض"], ["earth fault", "عطل أرضي"], ["earth leakage", "تسريب أرضي"],
    ["overvoltage", "ارتفاع الجهد"], ["over-voltage", "ارتفاع الجهد"], ["undervoltage", "انخفاض الجهد"],
    ["under-voltage", "انخفاض الجهد"], ["overload", "حمل زائد"], ["overtemperature", "ارتفاع حرارة"],
    ["overheat", "ارتفاع حرارة"], ["phase loss", "فقدان فازة"], ["communication", "خطأ اتصال"],
    ["encoder", "عطل إنكودر"], ["feedback", "عطل Feedback"], ["overspeed", "زيادة سرعة"], ["stalled", "انحشار المحرك"],
    ["fan", "عطل مروحة"], ["memory", "عطل ذاكرة"], ["eeprom", "عطل EEPROM"], ["parameter", "خطأ معلمات"],
    ["autotune", "فشل الضبط التلقائي"], ["brake", "عطل فرملة"], ["thermistor", "عطل حساس حرارة"],
    ["safe torque", "حالة/عطل STO"], ["external fault", "عطل خارجي"], ["cpu", "عطل CPU"], ["no fault", "لا يوجد عطل"],
  ];
  const found = map.find(([key]) => t.includes(key));
  return `${found?.[1] ?? (t.includes("fault") || t.includes("error") ? "عطل" : t.includes("alarm") ? "إنذار" : "حالة VFD")} — ${title}`;
}

function diagnostic(title: string) {
  const t = title.toLowerCase();
  if (["overcurrent", "over-current", "short", "ground fault", "earth fault", "earth leakage", "phase u to", "phase v to", "phase w to"].some(k => t.includes(k))) return {
    cause_ar: "تيار غير طبيعي أو قصر/تسريب في خرج الدرايف، وقد يكون السبب كابل المحرك أو ملفات المحرك أو الحمل الميكانيكي أو مرحلة القدرة.",
    steps: ["سجّل ظروف حدوث الكود ثم أوقف الدرايف.", "افصل التغذية وتأكد من تفريغ DC Bus.", "افحص U/V/W بحثاً عن قصر أو تسريب إلى الأرض.", "افحص مقاومة وعزل ملفات المحرك وقارن الفازات.", "تحقق من الحمل الميكانيكي وأزمنة التسارع/التباطؤ وإعدادات المحرك."],
    solutions: ["إصلاح القصر أو التسريب أو الكابل التالف.", "إصلاح المحرك أو الحمل عند ثبوت الخلل.", "تصحيح بيانات المحرك أو أزمنة الـRamp.", "إذا استمر العطل بعد فصل المحرك، افحص مرحلة القدرة بواسطة جهة مؤهلة."],
    parts: ["المحرك", "كابل المحرك", "IGBT / Power Module", "الحمل الميكانيكي"]
  };
  if (["overvoltage", "over-voltage", "under voltage", "undervoltage", "under-voltage", "low voltage", "power loss", "dc bus", "dc link"].some(k => t.includes(k))) return {
    cause_ar: "جهد المصدر أو دائرة DC Bus خارج المجال المسموح، أو توجد فازة مفقودة/هبوط مصدر أو طاقة راجعة أثناء التباطؤ.",
    steps: ["قِس جهد الدخل بين الفازات وتحقق من التوازن.", "افحص القاطع والفيوزات ونقاط التوصيل.", "راجع زمن التباطؤ والحمل ذي القصور الذاتي.", "افحص Brake Resistor/Chopper إذا كان مستخدماً."],
    solutions: ["معالجة ارتفاع/انخفاض المصدر أو الفازة المفقودة.", "زيادة زمن التباطؤ عند وجود Regeneration.", "إصلاح دائرة الكبح أو نقاط القدرة عند الحاجة."],
    parts: ["Input Supply", "DC Bus", "Fuse / Breaker", "Brake Resistor / Chopper"]
  };
  if (["phase loss", "open phase"].some(k => t.includes(k))) return {
    cause_ar: "إحدى فازات الدخل أو الخرج مفقودة أو غير متوازنة بسبب فيوز/قاطع/سلك أو نقطة توصيل أو ملف محرك مفتوح.",
    steps: ["حدّد هل الكود يخص الدخل أم الخرج.", "افحص الفازات الثلاث أو استمرارية U/V/W بعد العزل.", "افحص الفيوزات والـTerminals والكابل.", "افحص ملفات المحرك إذا كان الخلل في الخرج."],
    solutions: ["إرجاع الفازة المفقودة.", "إصلاح السلك أو الطرف المرتخي/المحترق.", "إصلاح المحرك عند وجود Open Winding."],
    parts: ["Input/Output Terminals", "Fuse", "Motor Cable", "Motor Windings"]
  };
  if (["overload", "stalled", "stall", "torque limit", "over torque", "underload", "load slipping", "mechanical"].some(k => t.includes(k))) return {
    cause_ar: "الحمل أو العزم خارج الحدود المسموحة أو يوجد انحشار/فقد حمل ميكانيكي أو إعداد حماية غير مناسب.",
    steps: ["قِس تيار المحرك وقارنه ببيانات اللوحة.", "افحص الحمل ميكانيكياً.", "راجع بيانات المحرك وحدود التيار/العزم.", "راجع أزمنة التسارع والتباطؤ."],
    solutions: ["إزالة الانحشار أو تخفيف الحمل.", "تصحيح بيانات المحرك وحدود الحماية.", "استخدام Drive/Motor بالحجم المناسب إذا كان الحمل أعلى من التصميم."],
    parts: ["Motor", "Mechanical Load", "Parameters"]
  };
  if (["overtemp", "over temperature", "overheat", "over-heat", "heat sink", "heatsink", "fan", "thermistor", "ptc", "temperature"].some(k => t.includes(k))) return {
    cause_ar: "درجة حرارة الدرايف/المحرك غير طبيعية أو توجد مشكلة تهوية أو مروحة أو حساس حرارة.",
    steps: ["افحص حرارة المحيط والتهوية.", "نظف المشتت وفتحات الهواء.", "افحص المروحة وتوصيلها.", "افحص PTC/thermistor عند ارتباط الكود بالمحرك.", "تأكد من عدم وجود Overload."],
    solutions: ["تحسين التهوية وتنظيف المشتت.", "إصلاح/استبدال المروحة أو الحساس.", "تخفيف الحمل أو تصحيح الحماية الحرارية."],
    parts: ["Fan", "Heatsink", "Temperature Sensor / PTC", "Motor"]
  };
  if (["communication", "fieldbus", "modbus", "ethernet", "network", "serial", "dsi", "keypad connection", "panel fault"].some(k => t.includes(k))) return {
    cause_ar: "انقطاع أو خطأ في الاتصال أو إعدادات البروتوكول أو كابل/وحدة الشبكة.",
    steps: ["افحص الكابل والموصلات والشيلد.", "راجع Address وBaud Rate وParity.", "تحقق من PLC/Master وOption Card.", "راجع Timeout وComm Loss Action."],
    solutions: ["إصلاح/استبدال كابل الاتصال.", "تصحيح إعدادات الشبكة.", "إعادة تثبيت أو استبدال Option Card إذا ثبت عطلها."],
    parts: ["Communication Cable", "PLC / Master", "Fieldbus Option Card"]
  };
  if (["encoder", "feedback", "speed deviation", "overspeed", "position", "pg error"].some(k => t.includes(k))) return {
    cause_ar: "إشارة Feedback غير صحيحة أو السرعة/الموضع خارج الحدود.",
    steps: ["افحص توصيل الإنكودر/Feedback ومصدر تغذيته.", "راجع نوع الإنكودر وعدد النبضات والاتجاه.", "افحص الاقتران الميكانيكي.", "راجع حدود السرعة والموضع."],
    solutions: ["إصلاح كابل أو تغذية الإنكودر.", "تصحيح إعدادات Feedback.", "استبدال الإنكودر عند ثبوت تلفه."],
    parts: ["Encoder / PG", "Feedback Cable", "Mechanical Coupling"]
  };
  if (["brake", "braking", "precharge", "rush current", "soft charge"].some(k => t.includes(k))) return {
    cause_ar: "خلل في دائرة الفرملة/الشحن المبدئي أو المقاومة/الترانزستور أو إشارات التحكم المرتبطة بها.",
    steps: ["افصل التغذية وافحص Brake Resistor وقيمتها وتوصيلها.", "افحص Brake Chopper/Transistor.", "راجع زمن التباطؤ ودورة التشغيل.", "افحص Precharge/contactor إذا كان الكود متعلقاً بالشحن."],
    solutions: ["إصلاح/استبدال مقاومة أو وحدة الفرملة.", "تصحيح زمن التباطؤ أو Duty Cycle.", "إصلاح دائرة Precharge."],
    parts: ["Brake Resistor", "Brake Chopper / IGBT", "Precharge Circuit"]
  };
  if (["eeprom", "memory", "parameter", "checksum", "config", "firmware", "flash", "cpu", "microprocessor", "hardware", "power unit", "board", "internal error", "system fault", "system abnormal", "driver error", "control fault"].some(k => t.includes(k))) return {
    cause_ar: "خطأ داخلي في المعلمات أو الذاكرة أو لوحة التحكم/القدرة أو توافق Firmware/Hardware.",
    steps: ["سجّل الكود وأي Subcode قبل Reset.", "افصل التغذية وأعد التشغيل مرة واحدة بعد التفريغ.", "تحقق من مصدر التحكم وعدم وجود تشويش أو توصيل خارجي خاطئ.", "راجع Backup المعلمات وتوافق Firmware/الوحدات."],
    solutions: ["استعادة/إعادة برمجة المعلمات من نسخة موثوقة.", "تحديث Firmware فقط بالإجراء الرسمي.", "إذا تكرر الخطأ الداخلي، افحص/استبدل الوحدة بواسطة صيانة مؤهلة."],
    parts: ["Control Board", "EEPROM / Memory", "Firmware", "Power Module"]
  };
  if (["autotune", "tuning", "identification", "id run", "motor static"].some(k => t.includes(k))) return {
    cause_ar: "عملية تعريف/ضبط المحرك لم تكتمل بسبب بيانات أو توصيل أو ظروف اختبار غير مناسبة.",
    steps: ["راجع بيانات Nameplate.", "تحقق من Star/Delta حسب الجهد.", "تأكد من شروط AutoTune المطلوبة في Manual.", "أعد الاختبار بعد إزالة سبب الإلغاء."],
    solutions: ["تصحيح بيانات وتوصيل المحرك.", "إعادة AutoTune/ID Run وفق تعليمات الموديل."],
    parts: ["Motor", "Motor Parameters", "Motor Cable"]
  };
  if (["safe torque", "safety", "sto", "emergency", "inhibit"].some(k => t.includes(k))) return {
    cause_ar: "وظيفة السلامة/STO فعالة أو يوجد خلل في دائرة السلامة.",
    steps: ["تحقق من سبب Emergency Stop/STO.", "افحص القناتين والأسلاك وSafety Relay.", "راجع تشخيصات السلامة للموديل."],
    solutions: ["إزالة سبب الإيقاف فقط بعد التأكد من أمان المعدة.", "إصلاح دائرة السلامة بواسطة شخص مؤهل."],
    parts: ["STO Inputs", "Safety Relay", "Emergency Stop"]
  };
  if (["analog", "4-20", "signal loss", "external fault", "external trip", "auxiliary input", "function loss"].some(k => t.includes(k))) return {
    cause_ar: "إشارة تحكم/حساس خارجية مفقودة أو دخل مبرمج كعطل أصبح فعالاً.",
    steps: ["حدّد المدخل المرتبط بالكود من Parameters.", "قِس 0-10V أو 4-20mA عند الحاجة.", "افحص الأسلاك والـCommon والحساس/PLC."],
    solutions: ["إصلاح الإشارة أو السلك أو الحساس.", "تصحيح إعداد وظيفة Loss/External Fault بعد معالجة السبب."],
    parts: ["Analog/Digital Input", "Sensor / Transmitter", "PLC Wiring"]
  };
  return {
    cause_ar: "الدرايف سجّل هذه الحالة وفق منطق الحماية أو التشخيص الخاص بالموديل. طابق الكود مع ظروف التشغيل والمعلمات المرتبطة به.",
    steps: ["سجّل ظروف حدوث الكود: السرعة والتيار والجهد والحمل.", "راجع المعلمات المرتبطة بالوظيفة المذكورة في اسم الكود.", "افحص التوصيلات والمكونات المرتبطة قبل Reset."],
    solutions: ["عالج السبب الفعلي ثم نفّذ Reset وفق دليل الشركة.", "إذا استمر الكود، استخدم إجراء الصيانة الرسمي للموديل."],
    parts: ["VFD", "Wiring", "Related Device"]
  };
}

function makeCodes(brand: string, model: string, source: string, rows: readonly RawCode[]): EquipmentCode[] {
  return rows.map(([code, title]) => {
    const d = diagnostic(title);
    return {
      id: `vfd-${slugPart(brand)}-${slugPart(model)}-${slugPart(code)}`,
      domain: "vfd", brand, model, code, meaning_ar: meaningAr(title),
      cause_ar: d.cause_ar, steps: d.steps, solutions: d.solutions, parts: d.parts,
      safety_ar: title.toLowerCase().includes("sto") || title.toLowerCase().includes("safe torque") ? "ممنوع تجاوز أو جسر دائرة STO/Emergency Stop كحل للعطل. اتبع Lockout/Tagout وتعليمات الشركة." : SAFETY,
      keywords: `${brand} ${model} ${code} ${title} ${source}`
    };
  });
}

const raw_abb_acs580: readonly RawCode[] = [
  ["64FF", "Fault reset"],
  ["B686", "Checksum mismatch"],
  ["A2A1", "Current calibration"],
  ["A2B1", "Overcurrent"],
  ["A2B3", "Earth leakage"],
  ["A2B4", "Short circuit"],
  ["A2BA", "IGBT overload"],
  ["A3A1", "DC link overvoltage"],
  ["A3A2", "DC link undervoltage"],
  ["A3AA", "DC not charged"],
  ["A490", "Incorrect temperature"],
  ["A491", "External temperature"],
  ["A492", "External temperature"],
  ["A4A0", "Control board"],
  ["A4A1", "IGBT overtemperature"],
  ["A4A9", "Cooling"],
  ["A4B0", "Excess temperature"],
  ["A4B1", "Excess temperature"],
  ["A4F6", "IGBT temperature"],
  ["A581", "Fan"],
  ["A582", "Auxiliary fan missing"],
  ["A5A0", "Safe torque off"],
  ["A5EA", "Measurement circuit"],
  ["A5EB", "PU board powerfail"],
  ["A5ED", "Measurement circuit"],
  ["A5EE", "Measurement circuit"],
  ["A5EF", "PU state feedback"],
  ["A5F0", "Charging feedback"],
  ["A682", "Flash erase speed"],
  ["A686", "Checksum mismatch"],
  ["A6A4", "Motor nominal value"],
  ["A6A5", "No motor data"],
  ["A6A6", "Voltage category"],
  ["A6A7", "System time not set"],
  ["A6B0", "User lock is open"],
  ["A6B1", "User pass code not"],
  ["A6D1", "FBA A parameter"],
  ["A6E5", "AI parametrization"],
  ["A6E6", "ULC configuration"],
  ["A780", "Motor stall"],
  ["A783", "Motor overload"],
  ["A784", "Motor disconnect"],
  ["A793", "BR excess"],
  ["A794", "BR data"],
  ["A79C", "BC IGBT excess"],
  ["A7A2", "Mechanical brake"],
  ["A7AB", "Extension I/O"],
  ["A7C1", "FBA A communication"],
  ["A7CE", "EFB comm loss"],
  ["A7EE", "Panel loss"],
  ["A88F", "Cooling fan"],
  ["A8A0", "AI supervision"],
  ["A8A1", "RO life warning"],
  ["A8A2", "RO toggle warning"],
  ["A8B0", "ABB Signal"],
  ["A8B1", "ABB Signal"],
  ["A8B2", "ABB Signal"],
  ["A8B3", "ABB Signal"],
  ["A8B4", "ABB Signal"],
  ["A8B5", "ABB Signal"],
  ["A8BE", "ULC overload warning"],
  ["A8BF", "ULC underload"],
  ["A981", "External warning 1"],
  ["A982", "External warning 2"],
  ["A983", "External warning 3"],
  ["A984", "External warning 4"],
  ["A985", "External warning 5"],
  ["AF88", "Season configuration"],
  ["AF90", "Speed controller"],
  ["AFAA", "Autoreset"],
  ["AFE1", "Emergency stop (off2)"],
  ["AFE2", "Emergency stop (off1"],
  ["AFE9", "Start delay"],
  ["AFEB", "Run enable missing"],
  ["AFED", "Enable to rotate"],
  ["AFF6", "Identification run"],
  ["AFF8", "Motor heating active"],
  ["B5A0", "STO event"],
  ["D50A", "Running dry"],
  ["D50B", "Pipe fill-timeout"],
  ["D501", "No more available"],
  ["D502", "All motors interlocked"],
  ["D503", "VSD controlled PFC"],
  ["D505", "Max cleaning warning"],
  ["D506", "Pump cleaning not"],
  ["D507", "Pump cleaning"],
  ["D511", "Cavitation control Cavitation control warning. See"],
  ["D58B", "Gas pressure outside"],
  ["D590", "Restart delay"],
  ["D591", "Min. run time"],
  ["D602", "Cavitation tune Cavitation auto tune has"],
  ["1080", "Backup/Restore"],
  ["1081", "Rating ID fault"],
  ["2281", "Calibration"],
  ["2310", "Overcurrent"],
  ["2330", "Earth leakage"],
  ["2381", "IGBT overload"],
  ["3130", "Input phase loss"],
  ["3181", "Wiring or earth fault"],
  ["3210", "DC link overvoltage"],
  ["3220", "DC link undervoltage"],
  ["3381", "Output phase loss"],
  ["3385", "Autophasing"],
  ["4110", "Control board"],
  ["4210", "IGBT overtemperature"],
  ["4290", "Cooling"],
  ["42F1", "IGBT temperature"],
  ["4310", "Excess temperature"],
  ["4380", "Excess temperature"],
  ["4981", "External temperature"],
  ["4982", "External temperature"],
  ["4990", "CPTC-02 not found"],
  ["4991", "Safe motor"],
  ["5080", "Fan"],
  ["5081", "Auxiliary fan broken"],
  ["5090", "STO hardware failure"],
  ["5091", "Safe torque off"],
  ["5092", "PU logic error"],
  ["5093", "Rating ID mismatch"],
  ["5094", "Measurement circuit"],
  ["5089", "SMT circuit"],
  ["5098", "I/O communication"],
  ["50A0", "Fan"],
  ["5681", "PU communication"],
  ["5682", "Power unit lost"],
  ["5691", "Measurement circuit"],
  ["5692", "PU board powerfail"],
  ["5693", "Measurement circuit"],
  ["5697", "Charging feedback"],
  ["5698", "Unknown PU fault"],
  ["6181", "FPGA version"],
  ["6200", "Checksum mismatch"],
  ["6306", "FBA A mapping file"],
  ["6481", "Task overload"],
  ["6487", "Stack overflow"],
  ["64A1", "Internal file load"],
  ["64A4", "Rating ID fault"],
  ["64A6", "Adaptive program"],
  ["64B1", "Internal SSW fault"],
  ["64B2", "User set fault"],
  ["64B3", "Macro"],
  ["64E1", "Kernel overload"],
  ["6581", "Parameter system"],
  ["6591", "Backup/Restore"],
  ["65A1", "FBA A parameter"],
  ["6681", "EFB comm loss"],
  ["6682", "EFB config file"],
  ["6683", "EFB invalid"],
  ["6684", "EFB load fault"],
  ["6685", "EFB fault 2"],
  ["6686", "EFB fault 3"],
  ["6882", "Text 32-bit table"],
  ["6885", "Text file overflow"],
  ["7081", "Control panel loss"],
  ["7082", "I/O module comm loss Communication between IO"],
  ["7085", "Incompatible option"],
  ["7086", "AI Overvoltage"],
  ["7121", "Motor stall"],
  ["7122", "Motor overload"],
  ["7181", "Brake resistor"],
  ["7183", "BR excess"],
  ["7191", "BC short circuit"],
  ["7192", "BC IGBT excess"],
  ["7310", "Overspeed"],
  ["73B0", "Emergency ramp"],
  ["73F0", "Overfrequency"],
  ["7510", "FBA A communication"],
  ["8001", "ULC underload fault"],
  ["8002", "ULC overload fault"],
  ["80A0", "AI supervision"],
  ["80B0", "Signal supervision 1"],
  ["80B1", "Signal supervision 2"],
  ["80B2", "Signal supervision 3"],
  ["80B3", "Signal supervision 4"],
  ["80B4", "Signal supervision 5"],
  ["80B5", "Signal supervision 6"],
  ["9081", "External fault 1"],
  ["9082", "External fault 2"],
  ["9083", "External fault 3"],
  ["9084", "External fault 4"],
  ["9085", "External fault 5"],
  ["D401", "Max cleaning fault"],
  ["D404", "Running dry"],
  ["D405", "Pipe fill-timeout"],
  ["D40C", "Cavitation detected"],
  ["FA81", "Safe torque off 1"],
  ["FA82", "Safe torque off 2"],
  ["FF61", "ID run"],
  ["FF63", "STO diagnostics"],
  ["FF81", "FB A force trip"],
  ["FF8E", "EFB force trip"],
];

const raw_allen_bradley_powerflex_523: readonly RawCode[] = [
  ["F000", "No Fault"],
  ["F002", "Auxiliary Input"],
  ["F003", "Power Loss"],
  ["F004", "UnderVoltage"],
  ["F005", "OverVoltage"],
  ["F006", "Motor Stalled"],
  ["F007", "Motor Overload"],
  ["F008", "Heatsink OvrTmp"],
  ["F009", "CC OvrTmp"],
  ["F012", "HW OverCurrent"],
  ["F013", "Ground Fault"],
  ["F021", "Output Ph Loss"],
  ["F029", "Analog In Loss"],
  ["F033", "Auto Rstrt Tries"],
  ["F038", "Phase U to Gnd"],
  ["F039", "Phase V to Gnd"],
  ["F040", "Phase W to Gnd"],
  ["F041", "Phase UV Short"],
  ["F042", "Phase UW Short"],
  ["F043", "Phase VW Short"],
  ["F048", "Params Defaulted"],
  ["F063", "SW OverCurrent"],
  ["F064", "Drive Overload"],
  ["F070", "Power Unit"],
  ["F071", "DSI Net Loss"],
  ["F072", "Opt Net Loss"],
  ["F080", "Autotune Failure"],
  ["F081", "DSI Comm Loss"],
  ["F082", "Opt Comm Loss"],
  ["F094", "Function Loss"],
  ["F100", "Parameter Chksum"],
  ["F101", "External Storage"],
  ["F105", "C Connect Err"],
  ["F106", "Incompat C-P"],
  ["F107", "Replaced C-P"],
  ["F109", "Mismatch C-P"],
  ["F110", "Keypad Membrane"],
  ["F114", "uC Failure"],
  ["F122", "I/O Board Fail"],
  ["F125", "Flash Update Req"],
  ["F126", "NonRecoverablErr"],
  ["F127", "DSIFlashUpdatReq"],
];

const raw_control_techniques_emerson_unidrive_m700_m701_m702: readonly RawCode[] = [
  ["OI.AC", "AC overcurrent trip"],
  ["Over Volts", "DC bus overvoltage trip"],
  ["Encoder 2", "Encoder 2 trip"],
  ["User Program", "User program trip"],
  ["Brake Resistor", "Brake resistor overload / thermal trip"],
  ["Motor Over Temperature", "Motor overtemperature trip"],
  ["I/O Overload", "24 V user supply or digital output overload"],
];

const raw_danfoss_vlt_automationdrive_fc_302: readonly RawCode[] = [
  ["1", "10 Volts low"],
  ["2", "Live zero error"],
  ["3", "No motor"],
  ["4", "Mains phase loss"],
  ["5", "DC link voltage high"],
  ["6", "DC link voltage low"],
  ["7", "DC overvoltage"],
  ["8", "DC under voltage"],
  ["9", "Inverter overload"],
  ["10", "Motor overload temperature"],
  ["11", "Motor thermistor overtemp"],
  ["12", "Torque limit"],
  ["13", "Over current"],
  ["14", "Earth (ground) fault"],
  ["15", "Hardware mismatch"],
  ["16", "Short circuit"],
  ["17", "Control word timeout"],
  ["22", "Hoist mechanical brake"],
  ["23", "Internal fan fault"],
  ["24", "External fan fault"],
  ["25", "Brake resistor short circuit"],
  ["26", "Brake resistor power limit"],
  ["27", "Brake chopper fault"],
  ["28", "Brake check failed"],
  ["29", "Heat Sink temp"],
  ["30", "Motor phase U missing"],
  ["31", "Motor phase V missing"],
  ["32", "Motor phase W missing"],
  ["33", "Inrush fault"],
  ["34", "Fieldbus communication fault"],
  ["36", "Mains failure"],
  ["38", "Internal fault"],
  ["39", "Heat sink sensor"],
  ["40", "Overload of digital output terminal 27"],
  ["41", "Overload of digital output terminal 29"],
  ["42", "Overload of digital output on X30/6 or"],
  ["45", "Earth fault 2"],
  ["46", "Power card supply"],
  ["47", "24 V supply low"],
  ["48", "1.8 V supply low"],
  ["49", "Speed limit"],
  ["50", "AMA calibration failed"],
  ["51", "AMA check Unom and Inom"],
  ["52", "AMA low Inom"],
  ["53", "AMA motor too big"],
  ["54", "AMA motor too small"],
  ["55", "AMA parameter out of range"],
  ["56", "AMA interrupted by user"],
  ["57", "AMA internal fault"],
  ["58", "AMA Internal fault"],
  ["59", "Current limit"],
  ["60", "External interlock"],
  ["61", "Feedback error"],
  ["62", "Output frequency at maximum limit"],
  ["63", "Mechanical brake low"],
  ["64", "Voltage Limit"],
  ["65", "Control card over temperature"],
  ["66", "Heat sink temperature low"],
  ["67", "Option module configuration has changed"],
  ["68", "Safe Stop activated"],
  ["69", "Power card temperature"],
  ["70", "Illegal FC configuration"],
  ["71", "PTC 1 safe stop"],
  ["72", "Dangerous failure"],
  ["73", "Safe Stop auto restart"],
  ["76", "Power unit setup"],
  ["77", "Reduced power mode"],
  ["79", "Illegal power section configu-"],
  ["81", "CSIV corrupt"],
  ["82", "CSIV parameter error"],
  ["85", "Dang fail PB"],
  ["104", "Mixing fan fault"],
  ["243", "Brake IGBT"],
  ["244", "Heat Sink temperature"],
  ["245", "Heat sink sensor"],
  ["246", "Power card supply"],
  ["247", "Power card temperature"],
  ["248", "Illegal power section configuration"],
  ["250", "New spare part"],
  ["251", "New typecode"],
];

const raw_delta_vfd_family_official_user_manual_did_1206: readonly RawCode[] = [
  ["o.c.A", "Overcurrent during acceleration"],
  ["o.c.d", "Overcurrent during deceleration"],
  ["o.c.n", "Overcurrent during constant-speed operation"],
];

const raw_delta_c2000_c2000_plus: readonly RawCode[] = [
  ["oH1", "IGBT overtemperature"],
  ["oH2", "Heatsink overtemperature"],
  ["EF", "External Fault input"],
];

const raw_fuji_frenic_hvac: readonly RawCode[] = [
  ["OC1", "Instantaneous overcurrent during acceleration"],
  ["OC2", "Instantaneous overcurrent during deceleration"],
  ["OC3", "Instantaneous overcurrent at constant speed"],
  ["EF", "Earth fault"],
  ["OV1", "Overvoltage during acceleration"],
  ["OV2", "Overvoltage during deceleration"],
  ["OV3", "Overvoltage at constant speed"],
  ["LV", "Undervoltage"],
  ["LiN", "Input phase loss"],
  ["OPL", "Output phase loss"],
  ["OH1", "Heat sink overheat"],
  ["OH2", "External alarm"],
  ["OH3", "Inverter internal overheat"],
  ["OH4", "Motor protection PTC"],
  ["FUS", "Fuse blown"],
  ["PbF", "Charger circuit fault"],
  ["OL1", "Motor overload"],
  ["OLU", "Inverter overload"],
  ["Er1", "Memory error"],
  ["Er2", "Keypad communications error"],
  ["Er3", "CPU error"],
  ["Er4", "Option communications error"],
  ["Er5", "Option error"],
  ["Er6", "Operation protection"],
  ["Er7", "Tuning error"],
  ["Er8", "RS-485 communication error COM port 1"],
  ["ErP", "RS-485 communication error COM port 2"],
  ["ErF", "Data saving error during undervoltage"],
  ["ErH", "Hardware error"],
  ["CoF", "Power input disconnect detection"],
  ["ECL", "Customizable logic error"],
  ["ECF", "Enable circuit error"],
  ["PV1", "PID feedback error 1"],
  ["PV2", "PID feedback error 2"],
  ["PVA", "External PID feedback error A"],
  ["PVb", "External PID feedback error B"],
  ["PVC", "External PID feedback error C"],
  ["Pdr", "Drought protection"],
  ["roC", "Max-starts-per-hour control"],
  ["PoL", "End of curve protection"],
  ["rLo", "Anti jam"],
  ["FoL", "Filter clogging error"],
  ["LoK", "Password protection"],
  ["Err", "Mock alarm"],
  ["FAL", "DC fan locked"],
  ["OL", "Motor overload early warning"],
  ["OH", "Heat sink overheat early warning"],
  ["LiF", "Lifetime alarm"],
  ["rEF", "Reference command loss"],
  ["UTL", "Low torque output"],
  ["PTC", "PTC thermistor activated"],
  ["rTE", "Inverter life / cumulative motor run time"],
  ["CnT", "Inverter life / number of startups"],
  ["PA1", "PID warning 1"],
  ["PA2", "PID warning 2"],
  ["PAA", "External PID warning A"],
  ["PAb", "External PID warning B"],
  ["PAC", "External PID warning C"],
  ["SLA", "Mutual operation slave inverter alarm"],
  ["Lob", "Low battery"],
  ["dtL", "Data information loss"],
  ["Fod", "Forced operation"],
];

const raw_hitachi_wj_c1_wj200_compatible_basic_fault_table: readonly RawCode[] = [
  ["E01", "Overcurrent event during constant speed"],
  ["E02", "Overcurrent event during deceleration"],
  ["E03", "Overcurrent event during acceleration"],
  ["E04", "Overcurrent event during other conditions"],
  ["E05", "Motor overload protection"],
  ["E06", "Braking resistor overload protection"],
  ["E07", "Overvoltage protection"],
  ["E08", "EEPROM error"],
  ["E09", "Undervoltage error"],
  ["E10", "Current detection error"],
  ["E11", "CPU error"],
  ["E12", "External trip"],
  ["E13", "USP"],
  ["E14", "Ground fault"],
  ["E15", "Input overvoltage"],
  ["E19", "Thermal sensor error"],
  ["E21", "Inverter thermal trip"],
  ["E22", "CPU communication error"],
  ["E25", "Main circuit error"],
  ["E26", "Driver error"],
  ["E30", "Driver error / internal power stage"],
  ["E35", "Thermistor"],
  ["E36", "Braking error"],
  ["E37", "Safe Stop"],
  ["E38", "Low-speed overload"],
  ["E40", "Operator connection error"],
  ["E41", "Modbus communication error"],
  ["E43", "EzSQ invalid instruction"],
  ["E44", "EzSQ nesting depth error"],
  ["E45", "EzSQ execution error"],
  ["E50", "User-defined fault 0"],
  ["E51", "User-defined fault 1"],
  ["E52", "User-defined fault 2"],
  ["E53", "User-defined fault 3"],
  ["E54", "User-defined fault 4"],
  ["E55", "User-defined fault 5"],
  ["E56", "User-defined fault 6"],
  ["E57", "User-defined fault 7"],
  ["E58", "User-defined fault 8"],
  ["E59", "User-defined fault 9"],
  ["E60", "Option-slot fault 0"],
  ["E61", "Option-slot fault 1"],
  ["E62", "Option-slot fault 2"],
  ["E63", "Option-slot fault 3"],
  ["E64", "Option-slot fault 4"],
  ["E65", "Option-slot fault 5"],
  ["E66", "Option-slot fault 6"],
  ["E67", "Option-slot fault 7"],
  ["E68", "Option-slot fault 8"],
  ["E69", "Option-slot fault 9"],
  ["E80", "Encoder signal/disconnection"],
  ["E81", "Overspeed / maximum speed exceeded"],
  ["E83", "Positioning range error"],
  ["E98", "External STO fault"],
  ["E99", "Internal STO fault"],
];

const raw_invertek_optidrive_family: readonly RawCode[] = [
  ["4-20 F", "4-20 mA signal loss / wire break"],
  ["F-Ptc", "Motor PTC trip"],
  ["P-LOSS", "Input phase loss"],
  ["Out-F", "Output phase loss"],
  ["INhibit", "Safe Torque Off inputs open / drive inhibited"],
  ["O-I", "Output overcurrent / short circuit"],
  ["O-Volt", "DC bus overvoltage"],
  ["U-Volt", "DC bus undervoltage"],
  ["O-t", "Drive overtemperature"],
  ["U-t", "Drive undertemperature"],
  ["I_t-trP", "Motor thermal overload"],
  ["E-triP", "External trip"],
];

const raw_invt_goodrive20: readonly RawCode[] = [
  ["OUt1", "IGBT Ph-U fault"],
  ["OUt2", "IGBT Ph-V fault"],
  ["OUt3", "IGBT Ph-W fault"],
  ["OC1", "Over-current acceleration"],
  ["OC2", "Over-current deceleration"],
  ["OC3", "Over-current constant speed"],
  ["OV1", "Over-voltage acceleration"],
  ["OV2", "Over-voltage deceleration"],
  ["OV3", "Over-voltage constant speed"],
  ["UV", "DC bus Under-voltage"],
  ["OL1", "Motor overload"],
  ["OL2", "Inverter overload"],
  ["OL3", "Electrical overload"],
  ["SPI", "Input phase loss"],
  ["SPO", "Output phase loss"],
  ["OH1", "Rectifier overheat"],
  ["OH2", "IGBT overheat"],
  ["EF", "External fault"],
  ["CE", "Communication error"],
  ["ItE", "Current detection fault"],
  ["tE", "Autotuning fault"],
  ["EEP", "EEPROM fault"],
  ["PIDE", "PID feedback fault"],
  ["bCE", "Braking unit fault"],
  ["END", "Factory-set running time reached"],
  ["PCE", "Keypad communication error"],
  ["UPE", "Parameter upload error"],
  ["DNE", "Parameter download error"],
  ["ETH1", "Grounding short fault 1"],
  ["ETH2", "Grounding short fault 2"],
  ["LL", "Electronic underload fault"],
  ["PoFF", "System power off / low DC voltage"],
];

const raw_ls_electric_ig5a: readonly RawCode[] = [
  ["ERR", "Communication Error"],
  ["POT", "Output Phase Loss"],
  ["IOL", "Inverter Overload"],
  ["COL", "Input Phase Loss"],
  ["EEP", "Parameter Saver Error"],
  ["NBR", "Brake Control Error"],
  ["ETH", "Electronic Thermal"],
  ["COM", "Keypad Error"],
  ["OLT", "Overload Trip"],
  ["OCT", "OverCurrent"],
  ["GFT", "Ground Fault Current"],
  ["OC2", "Overcurrent2"],
  ["OVT", "Over Voltage"],
  ["LVT", "Low Voltage"],
  ["NTC", "NTC Open/Error"],
  ["OHT", "Inverter OverHeat"],
  ["HWT", "Hardware Fault"],
  ["FAN", "Cooling Fan Fault"],
  ["rEEP", "Remote parameter save error"],
];

const raw_mitsubishi_fr_d700: readonly RawCode[] = [
  ["OL", "Stall prevention / overcurrent warning"],
  ["oL", "Regenerative overvoltage stall prevention"],
  ["RB", "Regenerative brake pre-alarm"],
  ["TH", "Electronic thermal relay pre-alarm"],
  ["PS", "PU stop"],
  ["MT", "Maintenance timer alarm"],
  ["UV", "Undervoltage"],
  ["FN", "Fan alarm"],
  ["E.OC1", "Overcurrent trip during acceleration"],
  ["E.OC2", "Overcurrent trip during constant speed"],
  ["E.OC3", "Overcurrent trip during deceleration or stop"],
  ["E.OV1", "Regenerative overvoltage trip during acceleration"],
  ["E.OV2", "Regenerative overvoltage trip during constant speed"],
  ["E.OV3", "Regenerative overvoltage trip during deceleration or stop"],
  ["E.THT", "Inverter overload trip (electronic thermal)"],
  ["E.THM", "Motor overload trip (electronic thermal)"],
  ["E.FIN", "Heatsink overheat"],
  ["E.ILF", "Input phase loss"],
  ["E.OLT", "Stall prevention stop"],
  ["E.BE", "Brake transistor alarm detection"],
  ["E.GF", "Output side earth fault overcurrent at start"],
  ["E.LF", "Output phase loss"],
  ["E.OHT", "External thermal relay operation"],
  ["E.PTC", "PTC thermistor operation"],
  ["E.PE", "Parameter storage device fault"],
  ["E.PUE", "PU disconnection"],
  ["E.RET", "Retry count excess"],
  ["E.CPU", "CPU fault"],
  ["E.CDO", "Output current detection fault"],
  ["E.IOH", "Inrush current limit circuit fault"],
  ["E.AIE", "Analog input fault"],
  ["E.SAF", "Safety circuit fault"],
];

const raw_omron_mx2: readonly RawCode[] = [
  ["E01", "Over-current event while at constant speed"],
  ["E02", "Over-current event during deceleration"],
  ["E03", "Over-current event during acceleration"],
  ["E04", "Over-current event during other conditions"],
  ["E05", "Motor overload protection"],
  ["E06", "Braking resistor overload protection"],
  ["E07", "Over-voltage protection"],
  ["E08", "EEPROM error"],
  ["E09", "Under-voltage error"],
  ["E10", "Current detection error"],
  ["E11", "CPU error"],
  ["E12", "External trip"],
  ["E13", "USP"],
  ["E14", "Ground fault"],
  ["E15", "Input over-voltage"],
  ["E21", "Inverter thermal trip"],
  ["E22", "CPU communication error"],
  ["E25", "Main circuit error"],
  ["E30", "Driver error"],
  ["E35", "Thermistor"],
  ["E36", "Braking error"],
  ["E37", "Safe Stop"],
  ["E38", "Low-speed overload protection"],
  ["E40", "Operator connection error"],
  ["E41", "Modbus communication error"],
  ["E43", "Drive Programming invalid instruction"],
  ["E44", "Drive Programming nesting depth error"],
  ["E45", "Drive Programming execution error"],
  ["E50", "User trip 0"],
  ["E51", "User trip 1"],
  ["E52", "User trip 2"],
  ["E53", "User trip 3"],
  ["E54", "User trip 4"],
  ["E55", "User trip 5"],
  ["E56", "User trip 6"],
  ["E57", "User trip 7"],
  ["E58", "User trip 8"],
  ["E59", "User trip 9"],
  ["E60", "Option error 0"],
  ["E61", "Option error 1"],
  ["E62", "Option error 2"],
  ["E63", "Option error 3"],
  ["E64", "Option error 4"],
  ["E65", "Option error 5"],
  ["E66", "Option error 6"],
  ["E67", "Option error 7"],
  ["E68", "Option error 8"],
  ["E69", "Option error 9"],
  ["E80", "Encoder disconnection"],
  ["E81", "Excessive speed"],
  ["E83", "Positioning range error"],
];

const raw_schneider_electric_altivar_320_atv320: readonly RawCode[] = [
  ["ANF", "Load slipping"],
  ["ASF", "Angle Error"],
  ["BLF", "Brake Control"],
  ["BRF", "Brake Feedback"],
  ["CFF", "Incorrect Config"],
  ["CFI", "Invalid Configuration"],
  ["CFI2", "Configuration Transfer Error"],
  ["CNF", "Fieldbus Communication Interrupted"],
  ["COF", "CANopen Communication Interrupted"],
  ["CRF1", "Precharge Capacitor"],
  ["CSF", "Channel Switch Error"],
  ["DLF", "Dynamic Load Error"],
  ["EEF1", "EEPROM Control"],
  ["EEF2", "EEPROM Power"],
  ["EPF1", "External Error"],
  ["EPF2", "Fieldbus External Error"],
  ["FBE", "Function Block Error"],
  ["FBES", "Function Block Stop Error"],
  ["FCF1", "Output Contactor Closed"],
  ["FCF2", "Output Contactor Opened"],
  ["HCF", "Boards Compatibility"],
  ["HDF", "IGBT Desaturation"],
  ["ILF", "Internal Link Error"],
  ["INF1", "Internal Error 1"],
  ["INF2", "Internal Error 2"],
  ["INF3", "Internal Error 3"],
  ["INF4", "Internal Error 4"],
  ["INF6", "Internal Error 6"],
  ["INF9", "Internal Error 9"],
  ["INFA", "Internal Error 10"],
  ["INFB", "Internal Error 11"],
  ["INFE", "Internal Error 14"],
  ["LCF", "Input Contactor"],
  ["LFF3", "AI3 4-20mA loss"],
  ["OBF", "DC Bus Overvoltage"],
  ["OCF", "Overcurrent"],
  ["OHF", "Drive Overheating"],
  ["OLC", "Process Overload"],
  ["OLF", "Motor Overload"],
  ["OPF1", "Single Output Phase Loss"],
  ["OPF2", "Output Phase Loss"],
  ["OSF", "Supply Mains Overvoltage"],
  ["OTFL", "DI6=PTC Overheat"],
  ["PHF", "Input Phase Loss"],
  ["PTFL", "DI6=PTC Probe Error"],
  ["SAFF", "Safety Function Error"],
  ["SCF1", "Motor Short Circuit"],
  ["SCF3", "Ground Short Circuit"],
  ["SCF4", "IGBT Short Circuit"],
  ["SCFS", "Motor Short Circuit"],
  ["SLF1", "Modbus Communication Interrupted"],
  ["SLF2", "PC Communication Interruption"],
  ["SLF3", "HMI Communication Interruption"],
  ["SOF", "Motor Overspeed"],
  ["SPF", "Encoder Feedback Loss"],
  ["SSF", "Torque Limitation Error"],
  ["TJF", "IGBT Overheating"],
  ["TNF", "Autotuning Error"],
  ["ULF", "Process Underload"],
  ["USF", "Supply Mains Undervoltage"],
];

const raw_siemens_sinamics_g120c: readonly RawCode[] = [
  ["F01000", "Internal software error"],
  ["F01001", "Floating-point exception"],
  ["F01600", "STOP A initiated"],
  ["F01910", "Fieldbus setpoint timeout"],
  ["F07801", "Motor overcurrent"],
  ["F07900", "Motor blocked"],
  ["F07901", "Motor overspeed"],
  ["F07902", "Motor stalled"],
  ["F30001", "Power unit: Overcurrent"],
  ["F30002", "DC-link voltage too high"],
  ["F30003", "DC-link voltage too low"],
  ["F30004", "Power unit overtemperature"],
  ["F30005", "I2t overload"],
  ["F30015", "Motor feeder cable phase failure"],
  ["F30021", "Ground fault"],
  ["F30022", "Power unit Uce monitoring"],
];

const raw_toshiba_as3: readonly RawCode[] = [
  ["E", "Emergency off"],
  ["E-11", "Brake answer error"],
  ["E-12", "PG error"],
  ["E-13", "Abnormal speed error"],
  ["E-18", "Analog input disconnecting"],
  ["E-19", "CPU communication error"],
  ["E-20", "Over torque boost"],
  ["E-21", "CPU1 fault B"],
  ["E-22", "Embedded Ethernet fault"],
  ["E-23", "Option fault (slot A)"],
  ["E-24", "Option fault (slot B)"],
  ["E-25", "Option fault (slot C)"],
  ["E-26", "CPU2 fault"],
  ["E-29", "Control power option failure"],
  ["E-31", "Rush current suppression relay fault"],
  ["E-32", "PTC failure"],
  ["E-37", "Servo lock error"],
  ["E-39", "Servo lock error"],
  ["E-42", "Cooling fan fault"],
  ["E-43", "Communication timeout (embedded Ethernet)"],
  ["OC1", "Overcurrent during acceleration"],
  ["OC2", "Overcurrent during deceleration"],
  ["OC3", "Overcurrent during constant speed running"],
  ["OL1", "Inverter overload"],
  ["OL2", "Motor overload"],
  ["EF2", "Ground fault"],
];

const raw_vacon_vacon_20: readonly RawCode[] = [
  ["1", "Overcurrent"],
  ["2", "Overvoltage"],
  ["3", "Earth fault"],
  ["8", "System fault"],
  ["9", "Under voltage"],
  ["10", "Input phase fault"],
  ["11", "Output phase fault"],
  ["13", "Frequency converter under temperature"],
  ["14", "Frequency converter over temperature"],
  ["15", "Motor stalled"],
  ["16", "Motor over temperature"],
  ["17", "Motor underload"],
  ["22", "EEPROM checksum fault"],
  ["25", "Microcontroller watchdog fault"],
  ["27", "Back EMF protection"],
  ["29", "Thermistor fault"],
  ["34", "Internal bus communication"],
  ["35", "Application fault"],
  ["41", "IGBT Overtemperature"],
  ["50", "Analog input selected signal loss"],
  ["51", "External fault"],
  ["52", "Door Panel fault"],
  ["53", "Fieldbus fault"],
  ["54", "Slot fault"],
  ["55", "Wrong run fault (FWD/REV conflict)"],
  ["57", "Identification fault"],
  ["63", "Quick Stop"],
  ["111", "Temperature fault"],
];

const raw_veichi_ac70t: readonly RawCode[] = [
  ["64", "Too low voltage while stop"],
  ["10", "Too low voltage in run"],
  ["7", "Acceleration over-voltage"],
  ["8", "Deceleration over-voltage"],
  ["9", "Constant speed over-voltage"],
  ["28", "Over-voltage while stop"],
  ["4", "Acceleration over-current"],
  ["5", "Deceleration over-current"],
  ["6", "Constant speed over-current"],
  ["11", "Motor overload"],
  ["12", "Inverter overload"],
  ["1", "System abnormality"],
  ["16", "Inverter over-heat"],
  ["15", "Rectifier over-heat"],
  ["20", "Motor static detection fault"],
  ["21", "Memory fault"],
  ["69", "Memory fault"],
  ["30", "Reserved"],
  ["13", "Input side open phase"],
  ["65", "Input side open phase"],
  ["14", "Output side open phase"],
  ["E.Gnd", "Output earth fault"],
];

const raw_weg_cfw500: readonly RawCode[] = [
  ["A0046", "Motor overload alarm.  Settings of P0156, P0157, and P0158 are too low for the used"],
  ["A0047", "Overload alarm on the power pack with  Inverter output overcurrent."],
  ["A0050", "Overtemperature alarm from the power  High ambient temperature around the inverter (> 50 °C (> 122 °F))"],
  ["A0090", "External alarm via DIx (option \"No  Wiring on DI1 to DI8 inputs are open or have poor contact."],
  ["A0098", "It indicates interruption of self-tuning.  It indicates that the drive is disabled via DIx, when self-tuning"],
  ["A0128", "Alarm that indicates serial  Check network installation, broken cable or fault/poor contact"],
  ["A0133", "It indicates that the CAN interface has  Measure if there is voltage within the allowed range between the No Supply on CAN no supply between pins 1 and 5 of the pins 1 and 5 of the CAN interface connector."],
  ["A0134", "Bus off error detected on the CAN  Check for short-circuit on the CAN circuit transmission cable."],
  ["A0135", "CANopen communication error control  Check the times set on the master and on the slave to exchange"],
  ["A0136", "Alarm indicates that the DeviceNet  Set the switch that controls the master operation for Run"],
  ["A0137", "Alarm that indicates that one or more  Check the network master status."],
  ["A0138", "It indicates that the inverter received the  Check the network master status, ensuring it is in the run mode."],
  ["A0139", "It indicates interruption in the  Check if the network master is correctly configured and operating"],
  ["A0140", "It indicates error in the access to the  Check if the Profibus DP module is correctly fitted."],
  ["A0148", "Indicates error in data exchange  Check if Ethernet module is properly connected to product."],
  ["A0163", "Analog input signal AIx at 4 to 20 mA or  Current signal on the analog input AIx interrupted or null."],
  ["A0168", "Difference between Speed Reference  Inverter in Torque Current Limitation."],
  ["A0177", "Fan replacement alarm (P0045 > 50000  The heatsink fan maximum number of operating hours has Fan Replacement hours). been reached."],
  ["A0700", "No communication with remote HMI, but  Check if the communication interface with the HMI is properly"],
  ["A0702", "This failure occurs when there is a  Check if the drive General Enable command is active."],
  ["A0704", "It occurs when 2 or more SoftPLC  Check the user’s program logic."],
  ["A0706", "This failure occurs when a SoftPLC  Check the programming of the references in the Local and/or"],
  ["A0708", "SoftPLC application not running.  SoftPLC application stopped (P1001 = 0 and P1000 = 3)."],
  ["A0710", "This failure occurs when the user tries  Extension of the SoftPLC Prog. exceeded 8 KBytes."],
  ["A0750", "Alarm that indicates an analog input was  Parameter P0231 or P0236 was not programmed for 5 or 6."],
  ["A0752", "Alarm that indicates a digital input was  Parameter P0263 or P0264 or P0265 or P0266 was not"],
  ["A0754", "Alarm that indicates the origin of the  Main PID controller is enabled (P1017 in 1 or 2) and the CFW500"],
  ["A0756", "Alarm that indicates the origin of the  Main PID controller is enabled (P1017 in 1 or 2) and the CFW500"],
  ["A0758", "Alarm that indicates the parameter for  Parameter P0516 was not programmed for 13 (Hz) or 3 (rpm)."],
  ["A0760", "Alarm that indicates the process variable  Parameter P1030 is programmed for 1 and the value of the Low Level of the of the main PID controller has a low process variable of the main PID controller remained below"],
  ["A0762", "Alarm that indicates the process variable  Parameter P1030 is programmed for 1 and the value of the High Level in the of the main PID controller has a high process variable of the main PID controller remained above"],
  ["A0764", "Alarm that indicates the CFW500  Main PID controller is enabled and in automatic mode, and the Frequency Inverter in frequency inverter is in the sleep mode. motor speed remained below the speed programmed in P1036"],
  ["A0766", "Alarm that indicates the dry pump  Parameter P1042 is programmed for 1 and the pump driven"],
  ["A0768", "Alarm that indicates the broken belt  Parameter P1046 is programmed for 1 and the motor driven"],
  ["A0770", "Alarm that indicates the need of  Parameter P1050 is programmed for 1 and the operation time"],
  ["A0780", "Alarm that indicates an analog input was  Parameter P0231 or P0236 was not programmed for 8."],
  ["A0782", "Alarm that indicates a digital input was  Parameter P0263 or P0264 or P0265 or P0266 was not"],
  ["A0784", "Alarm that indicates an analog output  Parameter P0251 or P0254 was not programmed for 16."],
  ["A0786", "Alarm that indicates the process variable  Parameter P1075 is programmed for 1 and the value of the Low Level of the of the external PID controller has a low process variable of the external PID controller remained below"],
  ["A0788", "Alarm that indicates the process variable  Parameter P1075 is programmed for 1 and the value of the High Level of the of the external PID controller has a high process variable of the external PID controller remained above"],
  ["F0021", "Undervoltage fault on the intermediate  Wrong voltage supply; check if the data on the inverter label"],
  ["F0022", "Overvoltage fault on the intermediate  Wrong voltage supply; check if the data on the inverter label"],
  ["F0031", "Main control cannot set a  Plug-in module is damaged."],
  ["F0032", "Plug-in Module was incorrectly  Plug-In Module is damaged."],
  ["F0033", "Stator resistance setting fault P0409.  Stator resistance value in P0409 does not comply with the Self-tuning Fault inverter power."],
  ["F0048", "Overload fault on the power pack with  Inverter output overcurrent (>2xInom)."],
  ["F0068", "Overtemperature fault measured on the  Overload on the motor shaft."],
  ["F0070", "Overcurrent or short-circuit on the  Short-circuit between two motor phases."],
  ["F0072", "Motor overload fault (60 s in 1.5xInom).  P0156, P0157 and P0158 setting is too low in relation to the Motor Overload motor operating current."],
  ["F0074", "Ground overcurrent fault.  Short-circuit to the ground in one or more output phases."],
  ["F0076", "This fault indicates the motor presents  Motor wiring or connection error."],
  ["F0078", "Overtemperature fault measured on the  Overload on the motor shaft."],
  ["F0079", "Failure of absence of encoder signals.  Wiring between encoder and the accessory interface encoder"],
  ["F0080", "Fault related to the supervision algorithm  Electric noise."],
  ["F0084", "Fault related to the automatic  Poor contact in the connection between the main control and"],
  ["F0085", "Failure in the initialization of the plug-in  Defect on the plug-in module."],
  ["F0091", "External fault via DIx (\"No External Fault\"  Wiring on DI1 to DI8 inputs are open or have poor contact."],
  ["F0150", "Overspeed fault.  Wrong settings of P0161 and/or P0162."],
  ["F0151", "Main firmware version is different from  Blank memory on plug-in module (1st power-up)."],
  ["F0169", "Difference between Speed Reference  Inverter in Torque Current Limitation for too long."],
  ["F0179", "Internal fan with speed (P0036) under  Internal fan failure."],
  ["F0182", "Pulse feedback circuit fault of the output  Hardware identification fault; compare P0295 and P0296 to the Pulse Feedback Fault voltage. inverter identification label."],
  ["F0228", "Indicates fault in the serial  Pulse feedback input circuit fault."],
  ["F0233", "This failure indicates that the CAN  Measure if there is voltage within the allowed range between the No Supply on CAN interface has no supply between pins 1 pins 1 and 5 of the CAN interface connector."],
  ["F0234", "Bus off error detected on the CAN  Check for short-circuit on the CAN circuit transmission cable."],
  ["F0235", "CANopen communication error control  Check the times set on the master and on the slave to exchange"],
  ["F0236", "Fault indicates that the DeviceNet  Set the switch that controls the master operation for Run"],
  ["F0237", "Fault that indicates that one or more  Check the network master status."],
  ["F0238", "It indicates that the inverter received the  Check the network master status, ensuring it is in the run mode."],
  ["F0239", "It indicates interruption in the  Check if the network master is correctly configured and operating"],
  ["F0240", "It indicates fault in the access to the  Check if the Profibus DP module is correctly fitted."],
  ["F0700", "No communication with remote HMI, but  Check if the communication interface with the HMI is properly"],
  ["F0701", "No communication with the remote HMI;  Check that the HMI communication interface is properly"],
  ["F0709", "SoftPLC application not running.  SoftPLC application stopped (P1001 = 0 and P1000 = 3)."],
  ["F0710", "The size of the SoftPLC user’s program  The total size of the user’s program is 8 Kilobytes for V/f scalar"],
  ["F0711", "Fault found in SoftPLC user’s program.  SoftPLC user’s program stored on flash memory is corrupted."],
  ["F0763", "Fault that indicates the process variation  Parameter P1030 is programmed for 2 and the value of the High Level in the of the main PID controller has a high process variation of the main PID controller remained above"],
  ["F0767", "Fault that indicates the dry pump  Parameter P1042 is programmed for 2 and the pump driven"],
  ["F0769", "Fault that indicates the broken belt  Parameter P1046 is programmed for 2 and the motor driven"],
  ["F0771", "Fault that indicates the need of replacing  Parameter P1050 is programmed for 2 and the operation time"],
  ["F0773", "Indicates to the user that the plug-in  Plug-in Module installed does not match the plug-in module for"],
  ["F0787", "Fault that indicates the feedback of the  Parameter P1075 is programmed for 2 and the value of the Low Level of the external PID controller has a low value. process variation of the external PID controller remained below"],
  ["F0789", "Fault that indicates the feedback of the  Parameter P1075 is programmed for 2 and the value of the High Level of the external PID controller has a high value. process variation of the external PID controller remained above"],
];

const raw_yaskawa_ga500: readonly RawCode[] = [
  ["AEr", "Station Address Setting Error"],
  ["bAT", "Keypad Battery Low Voltage"],
  ["bb", "Baseblock"],
  ["bCE", "Bluetooth Communication Fault"],
  ["boL", "BrakingTransistor Overload Fault"],
  ["bUS", "Option Communication Error"],
  ["bUSy", "Busy"],
  ["CALL", "Serial Comm Transmission Error"],
  ["CE", "Modbus Communication Error"],
  ["CF", "Control Fault"],
  ["CoF", "Current Offset Fault"],
  ["CP1", "Comparator 1 Limit Fault"],
  ["CP2", "Comparator 2 Limit Fault"],
  ["CPEr", "Control Mode Mismatch"],
  ["CPF00", "Control Circuit Error"],
  ["CPF01", "Control Circuit Error"],
  ["CPF06", "Control Circuit Error (EEPROM Memory Data Error)"],
  ["CPF08", "Terminal Board Connection Error"],
  ["CPF38", "EEPROM Memory Data Error"],
  ["CPyE", "Error Writing Data"],
  ["CrST", "Remove RUN Command to Reset"],
  ["CSEr", "Control Mode Mismatch"],
  ["CyC", "MECHATROLINK CommCycleSettingErr"],
  ["CyPo", "Cycle Power to Accept Changes"],
  ["dCE1", "Communication Error1"],
  ["dCE2", "Communication Error2"],
  ["dEv", "Speed Deviation"],
  ["dFPS", "Drive Model Mismatch"],
  ["dnE", "Drive Disabled"],
  ["dv7", "Polarity Judge Timeout"],
  ["dWA2", "DriveWorksEZ Alarm 2"],
  ["dWA3", "DriveWorksEZ Alarm 3"],
  ["dWAL", "DriveWorksEZ Alarm"],
  ["dWF1", "EEPROM Memory DWEZ Data Error"],
  ["dWF2", "DriveWorksEZ Fault 2"],
  ["dWF3", "DriveWorksEZ Fault 3"],
  ["dWFL", "DriveWorksEZ Fault"],
  ["E5", "MECHATROLINK Watchdog Timer Err"],
  ["EF", "FWD/REV Run Command Input Error"],
  ["EF0", "Option Card External Fault"],
  ["EF1", "External Fault (Terminal S1)"],
  ["EF2", "External Fault (Terminal S2)"],
  ["EF3", "External Fault (Terminal S3)"],
  ["EF4", "External Fault (Terminal S4)"],
  ["EF5", "External Fault (Terminal S5)"],
  ["EF6", "External Fault (Terminal S6)"],
  ["EF7", "External Fault (Terminal S7)"],
  ["End1", "Excessive Rated Voltage Setting"],
  ["End2", "Iron Core Saturation Coefficient"],
  ["End3", "Rated Current Setting Alarm"],
  ["End4", "Adjusted Slip Calculation Error"],
  ["End5", "Resistance Tuning Error"],
  ["End6", "Leakage Inductance Alarm"],
  ["End7", "No-Load Current Alarm"],
  ["End8", "HFI Alarm"],
  ["End9", "Initial Pole Detection Alarm"],
  ["EP24v", "External Power 24V Supply"],
  ["Er-01", "Motor Data Error"],
  ["Er-02", "Drive in an Alarm State"],
  ["Er-03", "STOP Button was Pressed"],
  ["Er-04", "Resistance Tuning Error"],
  ["Er-05", "No-Load Current Alarm"],
  ["Er-08", "Rated Slip Error"],
  ["Er-09", "Acceleration Error"],
  ["Er-10", "Motor Direction Error"],
  ["Er-11", "Motor Speed Error"],
  ["Er-12", "Current Detection Error"],
  ["Er-13", "Leakage Inductance Alarm"],
  ["Er-14", "Motor Speed Error 2"],
  ["Er-15", "Torque Saturation Error"],
  ["Er-16", "Inertia ID Error"],
  ["Er-17", "Reverse Prohibited Error"],
  ["Er-18", "Back EMF Error"],
  ["Er-19", "PM Inductance Error"],
  ["Er-20", "Stator Resistance Error"],
  ["Er-25", "HighFreq Inject Param Tuning Err"],
  ["Err", "EEPROM Write Error"],
  ["FbH", "Excessive PID Feedback"],
  ["FbL", "PID Feedback Loss"],
  ["GF", "Ground Fault"],
  ["HCA", "High Current Alarm"],
  ["iFEr", "Communication Err"],
  ["L24v", "Loss of External Power 24 Supply"],
  ["LF", "Output Phase Loss"],
  ["LF2", "Output Current Imbalance"],
  ["LoG", "Log Com Error"],
  ["LSo", "Low Speed Motor Step-Out"],
  ["LT-1", "Cooling Fan Maintenance Time"],
  ["LT-2", "Capacitor Maintenance Time"],
  ["LT-3", "SoftChargeBypassRelay MainteTime"],
  ["LT-4", "IGBT Maintenance Time (50%)"],
  ["ndAT", "Model,VolClass,Capacity Mismatch"],
  ["nSE", "Node Setup Error"],
  ["oC", "Overcurrent"],
  ["oFA00", "Option Not Compatible with Port"],
  ["oH", "Heatsink Overheat"],
  ["oH1", "Heatsink Overheat"],
  ["oH2", "External Overheat (H1-XX=B)"],
  ["oH3", "Motor Overheat (PTC Input)"],
  ["oH4", "Motor Overheat Fault (PTC Input)"],
  ["oL1", "Motor Overload"],
  ["oL2", "Drive Overload"],
  ["oL3", "Overtorque Detection 1"],
  ["oL4", "Overtorque Detection 2"],
  ["oL5", "Mechanical Weakening Detection 1"],
  ["oL7", "High Slip Braking Overload"],
  ["oPE01", "Drive Capacity Setting Fault"],
  ["oPE02", "Parameter Range Setting Error"],
  ["oPE03", "Multi-Function Input Setting Err"],
  ["oPE05", "Run Cmd/Freq Ref Source Sel Err"],
  ["oPE07", "Analog Input Selection Error"],
  ["oPE08", "Parameter Selection Error"],
  ["oPE09", "PID Control Selection Fault"],
  ["oPE10", "V/f Data Setting Error"],
  ["oPE11", "Carrier Frequency Setting Error"],
  ["oPE13", "Pulse Monitor Selection Error"],
  ["oPE16", "Energy Saving Constants Error"],
  ["oPE33", "Digital Output Selection Error"],
  ["oPr", "Keypad Connection Fault"],
  ["oS", "Overspeed"],
  ["ov", "Overvoltage"],
  ["ovEr", "Too Many Parameters Changed"],
  ["PASS", "Modbus Communication Test"],
  ["PE1", "PLC Faults"],
  ["PE2", "PLC Faults"],
  ["PF", "Input Phase Loss"],
  ["PWEr", "DWEZ Password Mismatch"],
  ["rdEr", "Error Reading Data"],
  ["rF", "Braking Resistor Fault"],
  ["rH", "Braking Resistor Overheat"],
  ["rr", "Dynamic Braking Transistor Fault"],
  ["rUn", "Motor Switch during Run"],
  ["SC", "Short Circuit/IGBT Failure"],
  ["SCF", "Safety Circuit Fault"],
  ["SE", "Modbus Test Mode Error"],
  ["SEr", "Speed Search Retries Exceeded"],
  ["SToF", "Safe Torque OFF"],
  ["STPo", "Motor Step-Out Detected"],
  ["TiM", "Keypad Time Not Set"],
  ["TrPC", "IGBT Maintenance Time (90%)"],
  ["UL3", "Undertorque Detection 1"],
  ["UL4", "Undertorque Detection 2"],
  ["UL5", "Mechanical Weakening Detection 2"],
  ["Uv", "DC Bus Undervoltage"],
  ["Uv1", "DC Bus Undervoltage"],
  ["Uv2", "Control Power Undervoltage"],
  ["Uv3", "Soft Charge Answerback Fault"],
  ["vAEr", "Voltage Class, Capacity Mismatch"],
  ["vFyE", "Parameters do not Match"],
  ["CPF02", "Control Circuit Error"],
  ["CPF03", "Control Circuit Error"],
  ["CPF11", "Control Circuit Error"],
  ["CPF12", "Control Circuit Error"],
  ["CPF13", "Control Circuit Error"],
  ["CPF14", "Control Circuit Error"],
  ["CPF16", "Control Circuit Error"],
  ["CPF17", "Control Circuit Error"],
  ["CPF18", "Control Circuit Error"],
  ["CPF19", "Control Circuit Error"],
  ["CPF20", "Control Circuit Error"],
  ["CPF21", "Control Circuit Error"],
  ["CPF22", "Control Circuit Error"],
  ["CPF23", "Control Circuit Error"],
  ["CPF24", "Control Circuit Error"],
];

export const vfdCodes: EquipmentCode[] = [
  ...makeCodes("ABB", "ACS580", "ABB ACS580 firmware manual", raw_abb_acs580),
  ...makeCodes("Allen-Bradley", "PowerFlex 523", "Rockwell PowerFlex 523 Quick Start / fault table", raw_allen_bradley_powerflex_523),
  ...makeCodes("Control Techniques / Emerson", "Unidrive M700 / M701 / M702", "Nidec Control Techniques official downloads / guides", raw_control_techniques_emerson_unidrive_m700_m701_m702),
  ...makeCodes("Danfoss", "VLT AutomationDrive FC 302", "Danfoss FC 302 Operating Instructions - Warnings and Alarms", raw_danfoss_vlt_automationdrive_fc_302),
  ...makeCodes("Delta", "VFD family (official user manual DID 1206)", "Delta official User Manual DID 1206", raw_delta_vfd_family_official_user_manual_did_1206),
  ...makeCodes("Delta", "C2000 / C2000 Plus", "Delta C2000 Plus official catalog and FAQ", raw_delta_c2000_c2000_plus),
  ...makeCodes("Fuji", "FRENIC-HVAC", "Fuji Electric FRENIC-HVAC official manual", raw_fuji_frenic_hvac),
  ...makeCodes("Hitachi", "WJ-C1 / WJ200 compatible basic fault table", "Hitachi official WJ-C1 list/manual", raw_hitachi_wj_c1_wj200_compatible_basic_fault_table),
  ...makeCodes("Invertek", "Optidrive family", "Invertek official iKnow troubleshooting / user guide references", raw_invertek_optidrive_family),
  ...makeCodes("INVT", "Goodrive20", "INVT Goodrive20 official manual", raw_invt_goodrive20),
  ...makeCodes("LS Electric", "iG5A", "LS Electric iG5A official manual", raw_ls_electric_ig5a),
  ...makeCodes("Mitsubishi", "FR-D700", "Mitsubishi Electric FR-D700 official instruction manual", raw_mitsubishi_fr_d700),
  ...makeCodes("Omron", "MX2", "Omron MX2 official user manual", raw_omron_mx2),
  ...makeCodes("Schneider Electric", "Altivar 320 (ATV320)", "Schneider Electric ATV320 Programming Manual", raw_schneider_electric_altivar_320_atv320),
  ...makeCodes("Siemens", "SINAMICS G120C", "Siemens Industry Online Support G120C fault list", raw_siemens_sinamics_g120c),
  ...makeCodes("Toshiba", "AS3", "Toshiba TIC AS3 Trip Information and Remedies", raw_toshiba_as3),
  ...makeCodes("Vacon", "VACON 20", "VACON 20 official manual", raw_vacon_vacon_20),
  ...makeCodes("Veichi", "AC70T", "VEICHI AC70T Crane Purpose VFD Manual", raw_veichi_ac70t),
  ...makeCodes("WEG", "CFW500", "WEG CFW500 official programming/troubleshooting manual", raw_weg_cfw500),
  ...makeCodes("Yaskawa", "GA500", "Yaskawa GA500 official Technical Reference / troubleshooting table", raw_yaskawa_ga500),
];
